/* global downloadAttachmentUrl displayApprovalStatuses */

import React, { Fragment, useEffect, useState } from 'react';

import ApproversTable from 'Components/user/ApproversTable';
import { format } from 'date-fns';

import { RECORD_TYPES, recordReference } from './approvalsHelpers';
import { money } from './planHelpers';
import SlideOver from './SlideOver';

import { formatAndRoundMoney, formatFigure } from 'Helpers/formatService';
import { apiClient } from 'Helpers/network';
import { downloadSource } from 'Helpers/urlService';

import LoadingSpinner from 'CommonComponents/LoadingSpinner';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';

const EMPTY = '—';

/** A detail fetch refused with one of these means the record cannot be opened, not that it failed to load. */
const GONE_STATUSES = [403, 404];

const lineMoney = (value) => formatFigure(value, formatAndRoundMoney);

/** Format a raw date value as dd-MM-yyyy, falling back to the raw string / em dash. */
const formatDate = (value) => {
    if (!value) {
        return EMPTY;
    }

    const parsed = new Date(value);

    return isNaN(parsed.getTime()) ? value : format(parsed, 'dd-MM-yyyy');
};

/** A labelled read-only detail field; long text wraps and preserves newlines. */
const Field = ({ label, value }) => (
    <div className="tw-flex tw-flex-col tw-gap-0.5">
        <span className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-charcoal-50">{label}</span>
        <span className="tw-whitespace-pre-line tw-text-sm tw-text-charcoal">{value || EMPTY}</span>
    </div>
);

/** A grouped section with a heading, matching the face's detail layout. */
const Section = ({ title, children }) => (
    <div className="tw-flex tw-flex-col tw-gap-3">
        <h4 className="tw-text-sm tw-font-bold tw-text-charcoal">{title}</h4>
        {children}
    </div>
);

/** The per-venue custom-price breakdown revealed under a budget line that carries custom prices. */
const CustomPrices = ({ row }) => (
    <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
        <div>
            <div className="tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-charcoal-50">{$t('Original Custom Price by Venues')}</div>
            {(row.original_custom_prices || []).map((price, i) => (
                <div key={`ocp-${i}`} className="tw-font-mono tw-text-sm tw-text-charcoal">
                    {price.name}: {price.amount}
                </div>
            ))}
        </div>
        <div>
            <div className="tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-charcoal-50">{$t('Revised Custom Price by Venues')}</div>
            {(row.revised_custom_prices || []).map((price, i) => (
                <div key={`rcp-${i}`} className="tw-font-mono tw-text-sm tw-text-charcoal">
                    {price.name}: {price.amount}
                </div>
            ))}
        </div>
    </div>
);

/**
 * Face-styled budget-lines table for the EA/DC detail view — mirrors the legacy
 * PurchaseOrdersDetailedTable's columns (Original/Revised price, the three per-driver quantities,
 * Original/Revised totals and Variance) but in the drawer's own styling. Sourced from
 * `po.detailed_amount`; project rows are excluded, and the footer totals `final_price` + `variance`.
 * A line that carries custom prices is expandable (chevron) to reveal its per-venue breakdown.
 */
const LinesTable = ({ po }) => {
    const quantityLabels = [$t('Quantity 1'), $t('Quantity 2'), $t('Quantity 3')];
    const rows = (po.detailed_amount || []).filter((row) => !(row.type && row.type === 'project'));

    const [expanded, setExpanded] = useState({});
    const toggle = (index) => setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));

    const headCls = 'tw-px-3 tw-pb-2 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-charcoal-50 tw-whitespace-nowrap';
    const numHeadCls = `${headCls} tw-text-end`;
    const cellCls = 'tw-px-3 tw-py-2 tw-text-sm tw-text-charcoal tw-whitespace-nowrap';
    const numCellCls = `${cellCls} tw-text-end`;

    return (
        <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-text-left">
                <thead>
                    <tr>
                        <th className={headCls}>{$t('Department / Cost Centre')}</th>
                        <th className={headCls}>{$t('Budget Line')}</th>
                        <th className={headCls}>{$t('Currency')}</th>
                        <th className={numHeadCls}>{$t('Original Price')}</th>
                        {quantityLabels.map((name, index) => (
                            <th key={`oq-${index}`} className={numHeadCls}>{`${$t('Original')} ${name}`}</th>
                        ))}
                        <th className={numHeadCls}>{$t('Original Total')}</th>
                        <th className={numHeadCls}>{$t('Revised Price')}</th>
                        {quantityLabels.map((name, index) => (
                            <th key={`rq-${index}`} className={numHeadCls}>{`${$t('Revised')} ${name}`}</th>
                        ))}
                        <th className={numHeadCls}>{$t('Revised Total')}</th>
                        <th className={numHeadCls}>{$t('Variance')}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, index) => {
                        const hasCustom = row.original_custom_prices?.length > 0 || row.revised_custom_prices?.length > 0;
                        const isOpen = !!expanded[index];

                        return (
                            <Fragment key={index}>
                                <tr
                                    className={`tw-border-t tw-border-grey ${hasCustom ? 'tw-cursor-pointer hover:tw-bg-charcoal-10' : ''}`}
                                    onClick={hasCustom ? () => toggle(index) : undefined}
                                >
                                    <td className={cellCls}>
                                        <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                                            {hasCustom &&
                                                (isOpen ? (
                                                    <ChevronDownIcon className="tw-h-3 tw-w-3 tw-text-charcoal-50" />
                                                ) : (
                                                    <ChevronRightIcon className="tw-h-3 tw-w-3 tw-text-charcoal-50" />
                                                ))}
                                            <span>
                                                {row.department || EMPTY}
                                                {row.costcenter && ` / ${row.costcenter}`}
                                            </span>
                                        </span>
                                    </td>
                                    <td className={cellCls}>{row.budget_line || EMPTY}</td>
                                    <td className={cellCls}>{row.currency ? row.currency.code : EMPTY}</td>
                                    <td className={numCellCls}>{lineMoney(row.original_price)}</td>
                                    {quantityLabels.map((name, qi) => {
                                        const q = (row.original_quantity || [])[qi];

                                        return (
                                            <td key={`oqv-${qi}`} className={numCellCls}>
                                                {formatFigure(q?.amount, (x) => x, { precision: 4 })}
                                            </td>
                                        );
                                    })}
                                    <td className={numCellCls}>{lineMoney(row.original_total)}</td>
                                    <td className={numCellCls}>{lineMoney(row.revised_price)}</td>
                                    {quantityLabels.map((name, qi) => {
                                        const q = (row.revised_quantity || [])[qi];

                                        return (
                                            <td key={`rqv-${qi}`} className={numCellCls}>
                                                {formatFigure(q?.amount, (x) => x, { precision: 4 })}
                                            </td>
                                        );
                                    })}
                                    <td className={numCellCls}>{lineMoney(row.revised_total)}</td>
                                    <td className={numCellCls}>{lineMoney(row.variance)}</td>
                                </tr>
                                {hasCustom && isOpen && (
                                    <tr className="tw-bg-charcoal-10">
                                        <td colSpan="14" className="tw-px-3 tw-py-3">
                                            <CustomPrices row={row} />
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        );
                    })}
                    {rows.length === 0 && (
                        <tr className="tw-border-t tw-border-grey">
                            <td colSpan="14" className="tw-px-3 tw-py-3 tw-text-center tw-text-sm tw-text-charcoal-50">
                                {EMPTY}
                            </td>
                        </tr>
                    )}
                    <tr className="tw-border-t tw-border-grey tw-font-semibold">
                        <td className={cellCls} colSpan="12">
                            {$t('Total')}
                        </td>
                        <td className={numCellCls}>{formatFigure(po.final_price)}</td>
                        <td className={numCellCls}>{formatFigure(po.variance)}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

/**
 * The two values the Procurement dashboard publishes as page globals. A page that does not define
 * them passes its own through props instead.
 */
const globalAttachmentUrl = () => (typeof downloadAttachmentUrl !== 'undefined' ? downloadAttachmentUrl : null);
const globalApprovalStatuses = () => (typeof displayApprovalStatuses !== 'undefined' ? displayApprovalStatuses : undefined);

/**
 * Read-only "who has signed off" table. Mirrors the legacy modals, which feed the shared
 * ApproversTable from the show endpoint's `approval_history` (falling back to the raw
 * `approvers` relation) and translate pivot statuses via `approvalStatuses`.
 */
const ApprovalHistorySection = ({ approvers, status, approvalStatuses }) => (
    <Section title={$t('Approval History')}>
        <div className="tw-overflow-x-auto">
            <ApproversTable approvers={approvers || []} status={status} displayApprovalStatuses={approvalStatuses} />
        </div>
    </Section>
);

/**
 * Read-only cancellation summary — only rendered when the PO was cancelled and we know by whom
 * (mirrors the legacy EA modal's canceled-by table: Canceled By · Date · Status).
 */
const CanceledBySection = ({ po }) => {
    if (!po.canceled_at || !po.canceled_by) {
        return null;
    }

    return (
        <Section title={$t('Cancellation')}>
            <div className="tw-overflow-x-auto">
                <table className="tw-w-full tw-min-w-[24rem] tw-text-left">
                    <thead>
                        <tr className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-charcoal-50">
                            <th className="tw-px-3 tw-pb-2">{$t('Canceled By')}</th>
                            <th className="tw-px-3 tw-pb-2">{$t('Date')}</th>
                            <th className="tw-px-3 tw-pb-2">{$t('Status')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="tw-border-t tw-border-grey">
                            <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-charcoal">
                                {`${po.canceled_by.first_name ?? ''} ${po.canceled_by.last_name ?? ''}`.trim() || EMPTY}
                            </td>
                            <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-charcoal">{formatDate(po.canceled_at)}</td>
                            <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-charcoal">{po.status_label || EMPTY}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </Section>
    );
};

/**
 * Read-only detail body for a PO row (EA/DC) — "Expenditure Approval Details" fields. With no
 * `attachmentUrl` template the attachments are listed by name only, for a viewer the download
 * route would refuse.
 */
const PurchaseOrderDetail = ({ po, currencySymbol, approvers, attachmentUrl, approvalStatuses }) => {
    const getDownloadUrl = (poId, attachmentId) => (attachmentUrl ? attachmentUrl.replace('ORDER_ID', poId).replace('ATTACHMENT_ID', attachmentId) : null);

    const downloadAttachment = (e, poId, attachmentId, path) => {
        e.preventDefault();

        const url = getDownloadUrl(poId, attachmentId);

        if (url) {
            downloadSource(path, url);
        }
    };

    return (
        <div className="tw-flex tw-flex-col tw-gap-6">
            <Section title={$t('Details')}>
                <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                    <Field label={$t('Code')} value={po.code} />
                    <Field label={$t('Value')} value={money(currencySymbol, Number(po.final_price) || 0)} />
                    <Field label={$t('Title')} value={po.purchase_requisition ? po.purchase_requisition.title : EMPTY} />
                    <Field label={$t('Requestor')} value={po.requested_by ? po.requested_by.name || po.requested_by.email : EMPTY} />
                    <Field
                        label={$t('Budget Owner')}
                        value={po.owner ? po.owner.name || `${po.owner.first_name ?? ''} ${po.owner.last_name ?? ''}`.trim() : EMPTY}
                    />
                    <Field label={$t('Submission Date')} value={formatDate(po.submission_date)} />
                    <Field label={$t('Procurement method')} value={po.procurement_policy ? po.procurement_policy.method : EMPTY} />
                    <Field label={$t('Recommended Supplier')} value={po.supplier ? po.supplier.name : EMPTY} />
                    <Field label={$t('Contract Reference')} value={po.supplier_contract ? po.supplier_contract.contract_reference : $t('New Contract')} />
                    <Field label={$t('Start Date')} value={formatDate(po.contract_started_at)} />
                    <Field label={$t('End Date')} value={formatDate(po.contract_ended_at)} />
                </div>
            </Section>

            <Section title={$t('Description')}>
                <Field label={$t('Expenditure description')} value={po.description} />
                <Field label={$t('Overview of Procurement Process')} value={po.overview} />
                <Field label={$t('Responses / quotes')} value={po.responses} />
                <Field label={$t('Conflicts of Interest')} value={po.conflicts} />
                <Field label={$t('Risks')} value={po.risks} />
                <Field label={$t('Other Information')} value={po.other} />
            </Section>

            <Section title={$t('Budget lines')}>
                <LinesTable po={po} />
            </Section>

            <Section title={$t('Attachments')}>
                {po.attachments && po.attachments.length > 0 ? (
                    <ul className="tw-flex tw-flex-col tw-gap-1">
                        {po.attachments.map((attachment) => (
                            <li key={attachment.id}>
                                {attachmentUrl ? (
                                    <a
                                        href="#"
                                        onClick={(e) => downloadAttachment(e, po.id, attachment.id, attachment.path)}
                                        className="tw-text-sm tw-font-medium tw-text-blue hover:tw-underline"
                                    >
                                        {attachment.path}
                                    </a>
                                ) : (
                                    <span className="tw-text-sm tw-text-charcoal">{attachment.path}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="tw-text-sm tw-text-charcoal-50">{$t('No attachments')}</p>
                )}
            </Section>

            <ApprovalHistorySection approvers={approvers} status={po.status} approvalStatuses={approvalStatuses} />

            <CanceledBySection po={po} />
        </div>
    );
};

/** Read-only detail body for a PR row. */
const PurchaseRequisitionDetail = ({ pr, currencySymbol, approvers, approvalStatuses }) => (
    <div className="tw-flex tw-flex-col tw-gap-6">
        <Section title={$t('Details')}>
            <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                <Field label={$t('Code')} value={pr.pr_code} />
                <Field label={$t('Value')} value={money(currencySymbol, Number(pr.estimated_value) || 0)} />
                <Field label={$t('Title')} value={pr.title} />
                <Field label={$t('Requestor')} value={pr.opener_name} />
                <Field label={$t('Procurement method')} value={pr.procurement_policy_method} />
                <Field label={$t('Target Contract Date')} value={formatDate(pr.contract_date)} />
                <Field label={$t('Status')} value={pr.canceled_at ? $t('Canceled') : pr.status} />
            </div>
        </Section>

        <Section title={$t('Description')}>
            <Field label={$t('Description')} value={pr.description} />
        </Section>

        <ApprovalHistorySection approvers={approvers} status={pr.status} approvalStatuses={approvalStatuses} />
    </div>
);

/**
 * The read-only body for one record, from its show endpoint's payload (`{ pr, approval_history }`
 * or `{ po }`). Shared by the dashboard's drawer and the approver's record page on Home, so the two
 * cannot show the same record differently.
 */
export const RecordDetail = ({ type, payload, currencySymbol, attachmentUrl = globalAttachmentUrl(), approvalStatuses = globalApprovalStatuses() }) =>
    type === RECORD_TYPES.PR ? (
        <PurchaseRequisitionDetail
            pr={payload.pr}
            currencySymbol={currencySymbol}
            approvers={payload.approval_history || payload.approvers || payload.pr?.approvers || []}
            approvalStatuses={approvalStatuses}
        />
    ) : (
        <PurchaseOrderDetail
            po={payload.po}
            currencySymbol={currencySymbol}
            approvers={payload.po?.approval_history || payload.po?.approvers || []}
            attachmentUrl={attachmentUrl}
            approvalStatuses={approvalStatuses}
        />
    );

/**
 * IB-701 — read-only details drawer for an Approvals-register row. Opens over the register when the
 * eye / reference is clicked and fetches the row's existing show endpoint via apiClient (PR rows →
 * purchase-requisitions.show; EA/DC rows → purchase-orders.show). There are intentionally NO
 * approve/reject actions — approvals happen on the user's own approvals dashboard, not here.
 *
 * A row flagged `unavailable` (a linked record that is cancelled, withdrawn, deleted or not in this
 * event) is never fetched, and a fetch answered 403 or 404 reads the same way: the drawer says the
 * record is gone rather than that it has no details. Any other failure keeps the generic message.
 */
const ApprovalsDetailModal = ({ row, isOpen, onClose, currencySymbol }) => {
    const [loading, setLoading] = useState(false);
    const [payload, setPayload] = useState(null);
    const [gone, setGone] = useState(false);

    const unavailable = !!row?.unavailable;
    const detailUrl = unavailable ? null : (row?.detailUrl ?? null);

    useEffect(() => {
        if (!isOpen || !detailUrl) {
            setPayload(null);
            setGone(false);

            return undefined;
        }

        let active = true;
        setLoading(true);
        setPayload(null);
        setGone(false);

        apiClient
            .get(detailUrl, { expectedErrorStatuses: GONE_STATUSES })
            .then(({ data }) => {
                if (active) {
                    setPayload(data);
                }
            })
            .catch((error) => {
                if (active && GONE_STATUSES.includes(error?.response?.status)) {
                    setGone(true);
                }
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [isOpen, detailUrl]);

    const isPr = row?.type === RECORD_TYPES.PR;
    const title = unavailable ? $t('Record unavailable') : isPr ? $t('Purchase Requisition Details') : $t('Expenditure Approval Details');
    const fetched = isPr ? payload?.pr : payload?.po;
    const subtitle = row?.reference || (fetched ? recordReference(row?.type, isPr ? fetched.pr_code : fetched.code, fetched.id) : null);

    return (
        <SlideOver title={title} subtitle={subtitle} isOpen={isOpen} onClose={onClose}>
            {unavailable || gone ? (
                <p className="tw-py-6 tw-text-center tw-text-sm tw-text-charcoal-50">{$t('This record is no longer available.')}</p>
            ) : loading ? (
                <LoadingSpinner />
            ) : !payload ? (
                <p className="tw-py-6 tw-text-center tw-text-sm tw-text-charcoal-50">{$t('No details to display.')}</p>
            ) : (
                <RecordDetail type={row?.type} payload={payload} currencySymbol={currencySymbol} />
            )}
        </SlideOver>
    );
};

export default ApprovalsDetailModal;
